import { observable, runInAction } from 'mobx'
import {
  ApproveJobScopeChangeRequest,
  FinalizeJobScopeChangeRequest,
  GetJobScopeChangeRequest,
  GetJobScopeChangePriceBreakdownForAdminRequest,
  type JobScopeChange,
  ListJobScopeChangesRequest,
  GetJobScopeChangeStatusAggregationsRequest,
  RejectJobScopeChangeRequest,
  EditJobScopeChangeItemsRequest,
} from '@taxfyle/api-internal/internal/job_scope_change_pb'
import type { PrimeUserFn, PrimeWorkspaceFn } from '@/features/iam'
import { User } from '@/features/iam/users/User'
import { Workspace } from '@/features/iam/workspaces/Workspace'
import { collection } from '@/infra/collection'
import { type PrimeJobFn } from '@/features/jobs'
import { Id } from '@/infra/id'
import { safeTimestampToDate, numberToDecimal } from '@/utils/grpc-util'
import {
  ScopeChangeRequest,
  ScopeChangeRequestPriceBreakDown,
  type ScopeChangeRequestProps,
  type ScopeChangeRequestStatus,
} from '@/features/admin/scope-change-requests/ScopeChangeRequest'
import {
  mapResponderTypeProtoToResponderType,
  mapResponseTypeProtoToResponseType,
  mapStatusAggregationProtoToScopeChangeRequestByStatusCount,
  mapScopeChangeRequestItemProtoToProps,
  mapScopeChangeRequestPriceBreakDownProtoToProps,
  mapScopeChangeRequestStatusProtoToStatus,
} from '@/features/admin/scope-change-requests/scope-change-request-mappers'
import { type ScopeChangeRequestByStatusCount } from '@/features/admin/scope-change-requests/ScopeChangeRequestByStatusCount'
import { type AdminApi } from '@/features/admin/api/admin-api'
import { ScopeChangeRequestQueryStringBuilder } from '@/features/admin/scope-change-requests/scope-change-request-query-string-builder'
import { type ScopeChangeRequestSortField } from '@/features/admin/scope-change-requests/scope-change-request-table-columns'
import {
  type CursorPaged,
  type CursorPageQuery,
  type Direction,
} from '@/infra/paging'
import { BoolValue } from 'google-protobuf/google/protobuf/wrappers_pb'

export class ScopeChangeRequestStore {
  /**
   * Change requests collection.
   */
  readonly scopeChangeRequests = collection<
    ScopeChangeRequest,
    Id,
    ScopeChangeRequestProps
  >({
    getDataId: (d) => d.id,
    getModelId: (m) => m.id,
    create: () => new ScopeChangeRequest(),
  })

  /**
   * Number of scope change requests per status, from the last aggregation fetch.
   */
  @observable.ref
  accessor scopeChangeRequestByStatus: readonly ScopeChangeRequestByStatusCount[] =
    []

  constructor(
    private readonly _api: AdminApi,
    private readonly _primeWorkspace: PrimeWorkspaceFn,
    private readonly _primeUser: PrimeUserFn,
    private readonly _primeJob: PrimeJobFn,
  ) {}

  /**
   * Lists scope change requests.
   *
   * @param query
   * @param signal
   */
  async listScopeChangeRequests(
    query: ListScopeChangeRequestsQuery,
    signal?: AbortSignal,
  ): Promise<CursorPaged<ScopeChangeRequest>> {
    const request = new ListJobScopeChangesRequest()

    if (query.workspaceIds) {
      request.setWorkspaceIdsList(query.workspaceIds.map(Id.assert))
    }

    const filter = new ScopeChangeRequestQueryStringBuilder()
      .withStatuses(query.statuses)
      .build()
    if (filter) {
      request.setFilter(filter)
    }

    if (query.sort) {
      const { field, direction } = query.sort
      request.setOrderBy(
        `${mapSortFieldToOrderByField(field)} ${direction ?? ''}`,
      )
    }

    if (query.limit) {
      request.setPageSize(query.limit)
    }

    if (query.after) {
      request.setPageToken(query.after)
    }

    const result =
      await this._api.scopeChangeRequests.listJobScopeChanges(request)
    signal?.throwIfAborted()

    return {
      data: result
        .getJobScopeChangesList()
        .map((op) =>
          this.scopeChangeRequests.set(
            this._mapScopeChangeRequestProtoToProps(op),
          ),
        ),
      next: result.getNextPageToken(),
    }
  }

  /**
   * Fetches a single scope change request.
   */
  async fetchScopeChangeRequest(
    scopeChangeRequestId: Id,
    signal?: AbortSignal,
  ): Promise<ScopeChangeRequest> {
    const getScopeChangeRequest = new GetJobScopeChangeRequest().setId(
      Id.assert(scopeChangeRequestId),
    )

    const updated = await this._api.scopeChangeRequests.getJobScopeChange(
      getScopeChangeRequest,
    )
    signal?.throwIfAborted()

    return this.scopeChangeRequests.set(
      this._mapScopeChangeRequestProtoToProps(updated),
    )
  }

  /**
   * Fetches the price breakdown of a scope change request.
   */
  async fetchScopeChangeRequestPriceBreakDown(
    scopeChangeRequest: ScopeChangeRequest,
    signal?: AbortSignal,
  ): Promise<ScopeChangeRequestPriceBreakDown> {
    const getJobScopeChangePriceBreakdownForAdminRequest =
      new GetJobScopeChangePriceBreakdownForAdminRequest().setId(
        Id.assert(scopeChangeRequest.id),
      )

    const updated =
      await this._api.scopeChangeRequests.getJobScopeChangePriceBreakdownForAdmin(
        getJobScopeChangePriceBreakdownForAdminRequest,
      )
    signal?.throwIfAborted()

    return new ScopeChangeRequestPriceBreakDown(
      mapScopeChangeRequestPriceBreakDownProtoToProps(updated),
    )
  }

  /**
   * Replaces the items of a scope change request.
   */
  async editScopeChangeRequest({
    scopeChangeRequestId,
    customItems,
    inventoryItems,
  }: EditScopeChangeRequestParams): Promise<void> {
    const customScopeChangeItemsList = customItems.map((i) =>
      new EditJobScopeChangeItemsRequest.NewJobScopeChangeCustomItemRequest()
        .setName(i.name)
        .setQuantity(i.quantity)
        .setPrice(numberToDecimal(i.price))
        .setProPrice(numberToDecimal(i.proPrice)),
    )

    const inventoryItemsRequest = inventoryItems.map((i) =>
      new EditJobScopeChangeItemsRequest.NewJobScopeChangeInventoryItemRequest()
        .setVersionId(Id.assert(i.versionId))
        .setQuantity(i.quantity),
    )

    const request = new EditJobScopeChangeItemsRequest()
      .setJobScopeChangeId(Id.assert(scopeChangeRequestId))
      .setNewCustomScopeItemsList(customScopeChangeItemsList)
      .setNewInventoryScopeItemsList(inventoryItemsRequest)

    await this._api.scopeChangeRequests.editJobScopeChangeItems(request)
  }

  /**
   * Finalize scope change request
   */
  async finalizeScopeChangeRequest(
    scopeChangeRequest: ScopeChangeRequest,
    items: ScopeChangeCustomItem[],
  ) {
    const request = new FinalizeJobScopeChangeRequest()
      .setJobId(Id.assert(scopeChangeRequest.job.id))
      .setItemsList(
        items.map((item) =>
          new FinalizeJobScopeChangeRequest.FinalizeJobScopeChangeItem()
            .setId(Id.assert(item.id))
            .setPrice(numberToDecimal(item.price))
            .setProPrice(numberToDecimal(item.proPrice)),
        ),
      )

    await this._api.scopeChangeRequests.finalizeJobScopeChange(request)

    await this.fetchScopeChangeRequest(scopeChangeRequest.id)
  }

  /**
   * Accept scope change request
   * @param scopeChangeRequest
   */
  async approveScopeChangeRequest(
    scopeChangeRequest: ScopeChangeRequest,
    skipClientApproval = false,
  ) {
    const request = new ApproveJobScopeChangeRequest().setJobId(
      Id.assert(scopeChangeRequest.job.id),
    )

    if (skipClientApproval) {
      const skipClientApprovalValue = new BoolValue().setValue(true)

      request.setSkipClientApproval(skipClientApprovalValue)
    }

    await this._api.scopeChangeRequests.approveJobScopeChange(request)

    await this.fetchScopeChangeRequest(scopeChangeRequest.id)
  }

  /**
   * Accepts scope change request on behalf of client
   * @param scopeChangeRequest
   */
  async acceptScopeChangeRequestOnBehalfOfClient(
    scopeChangeRequest: ScopeChangeRequest,
  ) {
    const request = new ApproveJobScopeChangeRequest().setJobId(
      Id.assert(scopeChangeRequest.job.id),
    )

    await this._api.scopeChangeRequests.approveJobScopeChangeOnBehalfOfClient(
      request,
    )

    await this.fetchScopeChangeRequest(scopeChangeRequest.id)
  }

  /**
   * Rejects scope change request on behalf of client
   * @param scopeChangeRequest
   * @param reason
   */
  async rejectScopeChangeRequestOnBehalfOfClient(
    scopeChangeRequest: ScopeChangeRequest,
    reason: string,
  ) {
    const request = new RejectJobScopeChangeRequest()
      .setJobId(Id.assert(scopeChangeRequest.job.id))
      .setReason(reason)

    await this._api.scopeChangeRequests.rejectJobScopeChangeOnBehalfOfClient(
      request,
    )

    await this.fetchScopeChangeRequest(scopeChangeRequest.id)
  }

  /**
   * Reject scope change request
   * @param scopeChangeRequest
   * @param reason
   */
  async rejectScopeChangeRequest(
    scopeChangeRequest: ScopeChangeRequest,
    reason: string,
  ) {
    const request = new RejectJobScopeChangeRequest()
      .setJobId(Id.assert(scopeChangeRequest.job.id))
      .setReason(reason)

    await this._api.scopeChangeRequests.rejectJobScopeChange(request)

    await this.fetchScopeChangeRequest(scopeChangeRequest.id)
  }

  /**
   * Fetches the number of scope change requests per status.
   */
  async fetchScopeChangeRequestStatusAggregations(
    workspaces?: Id[],
    signal?: AbortSignal,
  ): Promise<void> {
    let getScopeChangeRequestStatusAggregationsRequest =
      new GetJobScopeChangeStatusAggregationsRequest()

    if (workspaces?.length) {
      getScopeChangeRequestStatusAggregationsRequest =
        getScopeChangeRequestStatusAggregationsRequest.setWorkspaceIdsList(
          workspaces.map(Id.assert),
        )
    }

    const result =
      await this._api.scopeChangeRequests.getJobScopeChangeStatusAggregations(
        getScopeChangeRequestStatusAggregationsRequest,
      )

    signal?.throwIfAborted()

    runInAction(() => {
      this.scopeChangeRequestByStatus = result
        .getJobScopeChangeStatusAggregationList()
        .map(mapStatusAggregationProtoToScopeChangeRequestByStatusCount)
    })
  }

  private _mapScopeChangeRequestProtoToProps(
    proto: JobScopeChange,
  ): ScopeChangeRequestProps {
    const workspaceId = proto.getWorkspaceId()
    const createdByUserPublicId = proto.getCreatedByUserPublicId()

    return {
      id: Id.assert(proto.getId()),
      createTime: safeTimestampToDate(proto.getCreateTime()),
      job: this._primeJob({ id: Id.assert(proto.getJobId()) }),
      createdBy: createdByUserPublicId
        ? this._primeUser({ publicId: createdByUserPublicId })
        : User.none,
      status: mapScopeChangeRequestStatusProtoToStatus(proto.getStatus()),
      workspace: workspaceId
        ? (this._primeWorkspace({ id: workspaceId }) ?? Workspace.none)
        : Workspace.none,
      requireClientApproval: proto.getRequireClientApproval(),
      // Backend returns responders ordered most-recent-first.
      responders: proto.getRespondersList().map((r) => ({
        createdByUser: this._primeUser({
          publicId: r.getResponderUserPublicId(),
        }),
        responderType: mapResponderTypeProtoToResponderType(
          r.getResponderType(),
        ),
        responseType: mapResponseTypeProtoToResponseType(r.getResponseType()),
        dateCreated: safeTimestampToDate(r.getCreateTime()),
        reason: r.getReason()?.getValue() ?? null,
      })),
      reason: proto.getReason(),
      items: proto.getItemsList().map(mapScopeChangeRequestItemProtoToProps),
    }
  }
}

function mapSortFieldToOrderByField(field: ScopeChangeRequestSortField) {
  switch (field) {
    case 'CreateTime':
      return 'create_time'
    case 'Id':
      return 'id'
  }
}

/**
 * Query for listing scope change requests.
 */
export interface ListScopeChangeRequestsQuery extends CursorPageQuery {
  workspaceIds?: Id[]
  statuses?: ScopeChangeRequestStatus[]
  sort?: {
    field: ScopeChangeRequestSortField
    /**
     * Omitted to use the API's default direction for the field.
     */
    direction?: Direction
  }
}

/**
 * Params for replacing the items of a scope change request.
 */
export interface EditScopeChangeRequestParams {
  scopeChangeRequestId: Id
  customItems: EditScopeChangeCustomItemParams[]
  inventoryItems: EditScopeChangeInventoryItemParams[]
}

export interface ScopeChangeCustomItem {
  id: Id
  price: number
  proPrice: number
}

/**
 * Params for editing job scope changes.
 */
export interface EditScopeChangeCustomItemParams {
  name: string
  quantity: number
  price: number
  proPrice: number
}

export interface EditScopeChangeInventoryItemParams {
  versionId: Id
  quantity: number
}
